import Layout from 'shared/layouts/containers/Main'
import { Col, Row } from 'reactstrap'
import { NavLink, useNavigate } from 'react-router-dom'
import { roles } from 'consts'
import hasAnyRole from 'shared/utils/hasAnyRole'
import HeaderCustomerSelect from 'containers/HeaderCustomerSelect'
import resEditAccount from './resolvers/resEditAccount'
import { useForm } from 'react-hook-form'
import useInit from './hooks/useInit'
import React, { useState } from 'react'
import useFocusError from 'shared/hooks/useFocusError'
import { Form, Button } from 'react-bootstrap'
import prepareSubmitForm from './utils/prepareSubmitForm'
import submitForm from './utils/submitForm'
import InputWrapper from 'shared/components/InputWrapper'
import SelectWrapper from 'shared/components/SelectWrapper'
import useDynamicFilter from 'hooks/useDynamicFilter'
import getBrandContent from 'utils/getBrandContent'
import Footer from 'shared/components/Footer'
import { add } from 'ionicons/icons'
import { IonIcon } from '@ionic/react'
import showCustomerSwitcher from 'utils/showCustomerSwitcher'

const Account: React.FunctionComponent = () => {
  const [showOverlay, setShowOverlay] = useState<boolean>(true)
  const [dataLoaded, setDataLoaded] = useState<boolean>(false)

  const submitRef = React.createRef<HTMLButtonElement>()

  const [contacts, setContacts] = useState<any>([])
  const [states, setStates] = useState<any>([])
  const [allStates, setAllStates] = useState<any>(false)
  const [countries, setCountries] = useState<any>([])
  const [currentCountry, setCurrentCountry] = useState<any>(null)

  const [currentCustomer, setCurrentCustomer] = useState<{} | null>({})

  const navigate = useNavigate()

  const {
    control,
    register,
    getValues,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors },
    setValue,
    setError,
  } = useForm({
    mode: 'all',
    resolver: resEditAccount,
  })

  useInit({
    getValues,
    reset,
    setDataLoaded,
    setShowOverlay,
    setContacts,
    setStates,
    setAllStates,
    setCountries,
    setCurrentCountry,
    currentCustomer,
  })

  useFocusError({
    errors,
    setFocus,
  })

  useDynamicFilter({
    allRows: allStates,
    setFilteredRows: setStates,
    filterColumnName: 'country_id',
    inputValue: currentCountry,
    setDefaultFormFieldValue: setValue,
    formFieldValueName: 'state',
    isFormFieldValueNameObject: true,
    currentFormValues: getValues(),
    identityColumnName: 'id',
  })

  const isAdmin: boolean = hasAnyRole([roles.admin, roles.superAdmin])
  const isSuperAdmin: boolean = hasAnyRole([roles.superAdmin])

  return (
    <Layout
      footerContent={<Footer />}
      brandContent={getBrandContent()}
      showOverlay={showOverlay}
      showCardHeader={true}
      showMinimizedBarButton={true}
      headerInsert={
        showCustomerSwitcher() ? (
          <HeaderCustomerSelect
            setShowOverlay={setShowOverlay}
            handleCustomerChanged={(customer) => setCurrentCustomer(customer)}
          />
        ) : undefined
      }
      actionInsert={
        <>
          {isSuperAdmin && (
            <NavLink to='/add-customer' className='btn btn-primary mx-3'>
              <IonIcon
                icon={add}
                className='ionicon'
                style={{ fontSize: '16px', marginBottom: '-3px' }}
              />
              Add Customer
            </NavLink>
          )}
          {isAdmin && (
            <Button
              disabled={!dataLoaded}
              type='submit'
              name='save'
              className='btn-round btn-primary'
              onClick={() => {
                if (submitRef.current != null) {
                  submitRef.current.click()
                }
              }}
            >
              Save
            </Button>
          )}
        </>
      }
    >
      <>
        <Form
          onSubmit={handleSubmit((data) =>
            prepareSubmitForm({
              submitForm,
              setError,
              navigate,
              data,
              setShowOverlay,
              name: 'Account information',
            }),
          )}
          id='main-form'
        >
          <Row>
            <Col md={6}>
              <InputWrapper
                colAttributes={{ md: 12 }}
                label='Customer ID'
                name='id'
                errors={errors}
              >
                <Form.Control type='id' disabled={true} {...register('id')} />
              </InputWrapper>

              <InputWrapper colAttributes={{ md: 12 }} label='Name' name='name' errors={errors}>
                <Form.Control type='text' disabled={!isAdmin} {...register('name')} />
              </InputWrapper>

              <InputWrapper
                colAttributes={{ md: 12 }}
                label='Primary Contact'
                name='primaryContact'
                errors={errors}
              >
                <SelectWrapper
                  isDisabled={!isAdmin}
                  name='primaryContact'
                  control={control}
                  options={contacts}
                  optionLabel='concatened'
                  optionValue='id'
                  placeholder='Select Primary Contact...'
                />
              </InputWrapper>
            </Col>

            <Col md={6}>
              <InputWrapper colAttributes={{ md: 12 }} label='City' name='city' errors={errors}>
                <Form.Control type='text' disabled={!isAdmin} {...register('city')} />
              </InputWrapper>

              <InputWrapper
                colAttributes={{ md: 12 }}
                label='Country'
                name='country'
                errors={errors}
              >
                <SelectWrapper
                  isDisabled={!isAdmin}
                  name='country'
                  control={control}
                  options={countries}
                  optionLabel='name'
                  optionValue='id'
                  placeholder='Select Country...'
                  onChange={(option) => setCurrentCountry(option?.id)}
                />
              </InputWrapper>

              <InputWrapper
                colAttributes={{ md: 12 }}
                label='State/Province'
                name='state'
                errors={errors}
              >
                <SelectWrapper
                  isDisabled={!isAdmin}
                  name='state'
                  control={control}
                  options={states}
                  optionLabel='name'
                  optionValue='id'
                  placeholder='Select State...'
                />
              </InputWrapper>
            </Col>

            {isAdmin && (
              <Button type='submit' name='save' ref={submitRef} className='d-none'></Button>
            )}
          </Row>
        </Form>
      </>
    </Layout>
  )
}

export default Account
